import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Typography,
  Paper,
  Grid,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { styled } from '@mui/material/styles';
import { StatusChip } from '@components/StatusChip';
import { isWorkOrderLocked } from '@utils/workOrderHelpers';
import { useStationDevicesQuery } from '@api/bff/stationDevices/useStationDevices';
import type {
  WorkOrderViewModel,
  WorkOrderDetailViewModel
} from '@api/enterprise/openapi/models';

import {
  CustomerLocation,
  ServiceDetails,
  ScheduleStatus,
  Requirements,
  NotesSection
} from './components';
import { useTaskProgress } from './hooks';
import { useRackReadiness } from '../../hooks/useRackReadiness';
import { CustomerReviewButton } from '../CustomerReviewButton';
import { LockedStatusChip } from '../LockedStatusChip';

export type WorkOrderHeaderProps = {
  workOrder: WorkOrderViewModel;
  details: WorkOrderDetailViewModel[];
};

const SubtleCollapseButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  borderRadius: '50%',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.2rem'
  }
}));

export const WorkOrderHeader: React.FC<WorkOrderHeaderProps> = ({
  workOrder,
  details
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('workOrderDetails');
  const { statusCounts } = useTaskProgress(details);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isCollapsed, setIsCollapsed] = useState(false);

  const readiness = useRackReadiness(workOrder.jobNumber ?? '', details);
  const { robots, activeStationId } = useStationDevicesQuery();
  const activeRobot =
    robots.find(robot => robot.stationId === activeStationId) ?? null;
  const activeRobotLabel =
    activeRobot?.label || activeRobot?.stationId || t('rack.noActiveRobot');
  const readinessLabel = readiness.isReady
    ? t('rack.ready')
    : t('rack.unassignedCount', { count: readiness.unresolvedCount });

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const gridItemProps = isMobile
    ? { xs: 12 as const }
    : { xs: 12 as const, sm: 6 as const, md: 6 as const, lg: 3 as const };

  const isLocked = isWorkOrderLocked(workOrder.status);

  const notesContent = (
    <>
      <NotesSection
        notes={workOrder.notes || ''}
        editable={!isLocked}
        jobNumber={workOrder.jobNumber}
      />
      {workOrder.description && (
        <NotesSection notes={workOrder.description} isDescription />
      )}
    </>
  );

  const detailSections = (
    <>
      <Grid item {...gridItemProps}>
        <ServiceDetails workOrder={workOrder} details={details} />
      </Grid>
      <Grid item {...gridItemProps}>
        <ScheduleStatus workOrder={workOrder} statusCounts={statusCounts} />
      </Grid>
      <Grid item {...gridItemProps}>
        <Requirements workOrder={workOrder} />
      </Grid>
    </>
  );

  return (
    <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          pb: 1.5,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="primary.main">
          {workOrder.jobNumber}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isLocked && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={() =>
                navigate(`/work/${workOrder.jobNumber}/calibration`)
              }
            >
              {isMobile ? 'Run' : 'Run Script'}
            </Button>
          )}
          <CustomerReviewButton
            label={isMobile ? 'Review' : 'Customer Review'}
          />
          {readiness.occupiedCount > 0 && (
            <Chip
              label={readinessLabel}
              size="small"
              variant="outlined"
              color={readiness.isReady ? 'success' : 'warning'}
            />
          )}
          <Chip
            icon={<SmartToyIcon />}
            label={activeRobotLabel}
            size="small"
            variant="outlined"
            color={activeRobot ? 'default' : 'warning'}
          />
          {workOrder.status && (
            <StatusChip
              label={workOrder.status}
              variant="status"
              size="medium"
            />
          )}
          {isLocked && <LockedStatusChip />}
          {isMobile && (
            <SubtleCollapseButton
              onClick={toggleCollapse}
              aria-label={
                isCollapsed
                  ? 'Expand work order details'
                  : 'Collapse work order details'
              }
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </SubtleCollapseButton>
          )}
        </Box>
      </Box>

      {isMobile ? (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomerLocation workOrder={workOrder} />
            </Grid>
          </Grid>
          <Collapse in={!isCollapsed} timeout={300} unmountOnExit={false}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {detailSections}
            </Grid>
            {notesContent}
          </Collapse>
        </>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item {...gridItemProps}>
              <CustomerLocation workOrder={workOrder} />
            </Grid>
            {detailSections}
          </Grid>
          {notesContent}
        </>
      )}
    </Paper>
  );
};
